import { ReactNode, useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";

interface ParallaxHeroProps {
  imageUrl?: string;
  alt?: string;
  className?: string;
  overlayClassName?: string;
  children?: ReactNode;
  speed?: number;
  heightClass?: string;
}

const ParallaxHero = ({
  imageUrl,
  alt = "Hero background",
  className,
  overlayClassName,
  children,
  speed = 0.25,
  heightClass = "h-screen",
}: ParallaxHeroProps) => {
  const [offset, setOffset] = useState(0);
  const containerRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number>();

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) return; // Throttle with RAF

      rafRef.current = requestAnimationFrame(() => {
        if (!containerRef.current) {
          rafRef.current = undefined;
          return;
        }

        const rect = containerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Only apply parallax when element is in viewport
        if (rect.bottom < 0 || rect.top > windowHeight) {
          rafRef.current = undefined;
          return;
        }

        setOffset(window.scrollY * speed);
        rafRef.current = undefined;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [speed]);

  return (
    <section ref={containerRef} className={cn("relative w-full overflow-hidden bg-dark", heightClass, className)}>
      {/* Parallax Background */}
      <div className="absolute inset-0">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={alt}
            className="w-full h-[120%] object-cover will-change-transform"
            style={{
              transform: `translate3d(0, ${offset}px, 0) scale(1.1)`,
              filter: "brightness(0.4)",
            }}
          />
        ) : (
          <div className="w-full h-full bg-dark" />
        )}
        <div className={cn("hero-overlay", overlayClassName)} />
      </div>

      {/* Content */}
      {children}
    </section>
  );
};

export default ParallaxHero;
